import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useEscape, useScrollLock } from '../hooks/useAsync';
import { t } from '../i18n';
import { IconClose } from './icons';

interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md';
}

/** Replaces window.alert / window.confirm everywhere in the app. */
export function Modal({ open, title, description, onClose, children, footer, size = 'md' }: ModalProps) {
  const panel = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  useEscape(open, onClose);
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    restoreTo.current = document.activeElement as HTMLElement | null;
    const focusables = panel.current?.querySelectorAll<HTMLElement>(
      'input, textarea, select, button, [href], [tabindex]:not([tabindex="-1"])',
    );
    (focusables?.[0] ?? panel.current)?.focus();
    return () => restoreTo.current?.focus?.();
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="modal"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        style={{ maxWidth: size === 'sm' ? 420 : 560 }}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key !== 'Tab') return;
          const focusables = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
            'input:not(:disabled), textarea:not(:disabled), select:not(:disabled), button:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
          )).filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0);
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          if (!first) {
            event.preventDefault();
          } else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }}
      >
        <div className="modal__grab" aria-hidden="true" />
        <header className="modal__head">
          <div className="stack-sm">
            <h2 id="modal-title" style={{ fontSize: 'var(--fs-20)' }}>
              {title}
            </h2>
            {description ? <p className="small muted">{description}</p> : null}
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('ui.close')}>
            <IconClose />
          </button>
        </header>
        <div className="stack">{children}</div>
        {footer ? <footer className="row" style={{ marginTop: 'var(--sp-6)' }}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

interface ConfirmProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = t('ui.delete'),
  cancelLabel = t('ui.cancel'),
  danger = true,
  busy = false,
  onConfirm,
  onClose,
}: ConfirmProps) {
  return (
    <Modal
      open={open}
      title={title}
      description={description}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`btn ${danger ? 'btn--danger' : 'btn--primary'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? t('ui.waiting') : confirmLabel}
          </button>
        </>
      }
    >
      <p className="dim">{t('ui.irreversible')}</p>
    </Modal>
  );
}
